import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { ArrowRight, MapPin, User, Phone, CalendarBlank, Package, Ruler, Pause, CheckCircle } from '@phosphor-icons/react';
import { formatDate } from '../utils/dateFormat';

const STATUS_MAP = {
  pending_procurement: { label: 'Pending Procurement', cls: 'bg-amber-100 text-amber-700' },
  procurement_approved: { label: 'Approved', cls: 'bg-blue-100 text-blue-700' },
  procurement_rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
  assigned_to_production: { label: 'Assigned to Production', cls: 'bg-blue-100 text-blue-700' },
  under_production: { label: 'Under Production', cls: 'bg-purple-100 text-purple-700' },
  production_on_hold: { label: 'Production On Hold', cls: 'bg-amber-100 text-amber-700' },
  production_completed: { label: 'Ready to Dispatch', cls: 'bg-cyan-100 text-cyan-700' },
  ready_to_dispatch: { label: 'Ready to Dispatch', cls: 'bg-cyan-100 text-cyan-700' },
  scheduled_for_dispatch: { label: 'Scheduled for Dispatch', cls: 'bg-yellow-100 text-yellow-700' },
  partially_dispatched: { label: 'Partially Dispatched', cls: 'bg-orange-100 text-orange-700' },
  dispatched: { label: 'Dispatched', cls: 'bg-emerald-100 text-emerald-700' },
  pending_for_installation: { label: 'Pending Installation', cls: 'bg-indigo-100 text-indigo-700' },
  installation_completed: { label: 'Installation Completed', cls: 'bg-teal-100 text-teal-700' },
};

function InfoRow({ icon: Icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon size={18} weight="bold" className="text-zinc-400 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400">{label}</p>
        <p className="text-sm text-zinc-950 break-words">{value}</p>
      </div>
    </div>
  );
}

function HoldBanner({ jobOrder }) {
  if (jobOrder.status !== 'production_on_hold' || !jobOrder.hold_reason) return null;
  return (
    <div data-testid="side-panel-hold-banner" className="bg-amber-50 border border-amber-300 rounded-sm p-3">
      <div className="flex items-start gap-2">
        <Pause size={18} weight="bold" className="text-amber-600 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-amber-800">On Hold: {jobOrder.hold_reason}</p>
          {jobOrder.hold_remarks && <p className="text-xs text-amber-600 mt-0.5">{jobOrder.hold_remarks}</p>}
        </div>
      </div>
    </div>
  );
}

function ProductionTimeline({ jobOrder }) {
  if (!jobOrder.production_start_at) return null;
  return (
    <div className="space-y-1 border-b border-zinc-100 pb-4">
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400 mb-2">Production Timeline</p>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-xs text-zinc-400">Started</p>
          <p className="text-zinc-950 font-medium">{formatDate(jobOrder.production_start_at)}</p>
        </div>
        {jobOrder.production_end_at && (
          <div>
            <p className="text-xs text-zinc-400">Completed</p>
            <p className="text-zinc-950 font-medium">{formatDate(jobOrder.production_end_at)}</p>
          </div>
        )}
      </div>
      {jobOrder.production_duration_minutes && (
        <p className="text-xs text-purple-600 font-bold mt-1">
          Duration: {Math.floor(jobOrder.production_duration_minutes / 60)}h {Math.round(jobOrder.production_duration_minutes % 60)}m
        </p>
      )}
    </div>
  );
}

function DispatchInfo({ jobOrder }) {
  if (!jobOrder.windows_dispatched && !jobOrder.dispatch_schedule_date) return null;
  return (
    <div className="space-y-1 border-b border-zinc-100 pb-4">
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400 mb-2">Dispatch Info</p>
      {jobOrder.dispatch_schedule_date && (
        <div className="flex items-center gap-2 text-sm">
          <CalendarBlank size={16} className="text-yellow-600" />
          <span className="text-zinc-600">Scheduled:</span>
          <span className="font-bold text-yellow-700">{formatDate(jobOrder.dispatch_schedule_date) || jobOrder.dispatch_schedule_date}</span>
        </div>
      )}
      {jobOrder.windows_dispatched > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle size={16} className="text-emerald-600" />
          <span className="text-zinc-600">Dispatched:</span>
          <span className="font-bold">{jobOrder.windows_dispatched} / {jobOrder.no_of_windows} windows</span>
        </div>
      )}
    </div>
  );
}

export function JOSidePanel({ open, onClose, jobOrder }) {
  const navigate = useNavigate();
  if (!jobOrder) return null;

  const status = STATUS_MAP[jobOrder.status] || { label: jobOrder.status, cls: 'bg-zinc-100 text-zinc-600' };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="sm:max-w-lg w-full overflow-y-auto p-0">
        <SheetHeader className="p-5 pb-4 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center justify-between pr-6">
            <SheetTitle className="text-xl font-bold text-zinc-950">JO #{jobOrder.jo_number}</SheetTitle>
            <Badge data-testid={`side-panel-status-${jobOrder.jo_number}`} className={`${status.cls} text-xs`}>{status.label}</Badge>
          </div>
          <SheetDescription className="text-sm text-zinc-500">
            {jobOrder.customer_name} — {jobOrder.work_location}
          </SheetDescription>
        </SheetHeader>

        <div className="p-5 space-y-5">
          <HoldBanner jobOrder={jobOrder} />

          {/* Customer & Location */}
          <div className="space-y-1 border-b border-zinc-100 pb-4">
            <InfoRow icon={User} label="Customer" value={jobOrder.customer_name} />
            <InfoRow icon={Phone} label="Mobile" value={jobOrder.customer_mobile} />
            <InfoRow icon={MapPin} label="Location" value={jobOrder.work_location} />
            {jobOrder.executive_name && <InfoRow icon={User} label="Executive" value={jobOrder.executive_name} />}
          </div>

          {/* Job Details */}
          <div className="space-y-1 border-b border-zinc-100 pb-4">
            <InfoRow icon={CalendarBlank} label="Delivery Date" value={formatDate(jobOrder.delivery_date) || jobOrder.delivery_date} />
            <InfoRow icon={Package} label="Windows" value={jobOrder.no_of_windows} />
            <InfoRow icon={Ruler} label="Total Sqft" value={jobOrder.total_sqft} />
            {jobOrder.profile_type && <InfoRow icon={Package} label="Profile / Glass" value={`${jobOrder.profile_type} / ${jobOrder.glass_type}`} />}
          </div>

          <ProductionTimeline jobOrder={jobOrder} />
          <DispatchInfo jobOrder={jobOrder} />

          {/* View Full Details Button */}
          <Button
            data-testid={`side-panel-view-full-${jobOrder.jo_number}`}
            onClick={() => { onClose(); navigate(`/job-orders/${jobOrder.jo_number}`); }}
            className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-11"
          >
            View Full Details
            <ArrowRight size={18} weight="bold" className="ml-2" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function DrillDownJobList({ jobs, title, onSelectJO, loading }) {
  if (loading) return <div className="text-center py-8 text-zinc-500">Loading...</div>;
  if (!jobs || jobs.length === 0) return <div className="text-center py-8 text-zinc-400">No job orders found</div>;

  return (
    <div data-testid="drill-down-job-list" className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50">
            <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">JO No</th>
            <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Customer</th>
            <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 hidden md:table-cell">Location</th>
            <th className="text-left p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 hidden lg:table-cell">Delivery</th>
            <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500 hidden md:table-cell">Windows</th>
            <th className="text-center p-3 font-bold text-xs uppercase tracking-[0.15em] text-zinc-500">Action</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map(jo => (
            <tr
              key={jo.jo_number}
              data-testid={`drill-down-row-${jo.jo_number}`}
              className="border-b border-zinc-100 hover:bg-orange-50/50 cursor-pointer transition-colors"
              onClick={() => onSelectJO(jo)}
            >
              <td className="p-3 font-bold text-zinc-950">{jo.jo_number}</td>
              <td className="p-3 text-zinc-700">{jo.customer_name}</td>
              <td className="p-3 text-zinc-700 hidden md:table-cell">{jo.work_location}</td>
              <td className="p-3 text-zinc-700 hidden lg:table-cell">{formatDate(jo.delivery_date) || jo.delivery_date}</td>
              <td className="p-3 text-center text-zinc-700 hidden md:table-cell">{jo.no_of_windows}</td>
              <td className="p-3 text-center">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`drill-down-view-${jo.jo_number}`}
                  className="h-7 text-xs border-orange-300 text-orange-600 hover:bg-orange-50"
                  onClick={(e) => { e.stopPropagation(); onSelectJO(jo); }}
                >
                  View
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
